import { type FieldValues, FormProvider } from "react-hook-form";

import { Box } from "ui/box/box";
import { Button } from "ui/button/button";
import { LoadingButton } from "ui/button/loading-button/loading-button";
import { Dialog } from "ui/dialog/dialog";
import { DialogActions } from "ui/dialog/dialog-actions/dialog-actions";
import { Tooltip } from "ui/tooltip/tooltip";

import { useFormDialog } from "ui/form-dialog/use-form-dialog";

import { FORM_SX } from "ui/form-dialog/form-dialog.styles";
import { type FormDialogProps } from "ui/form-dialog/form-dialog.types";

export const FormDialog = <TValues extends FieldValues, TSubmitValues = TValues>({
  form,
  onSubmit,
  children,
  onClose,
  submitButtonText,
  submitLoadingMessage = "Submitting...",
  cancelButtonText = "Cancel",
  submitDisabled = false,
  submitting = false,
  submitTooltip,
  submitOnEnter = true,
  back,
  ...dialogProps
}: FormDialogProps<TValues, TSubmitValues>) => {
  const { formId, actionsType, isPending, isSubmitBlocked, handleSubmit, handleKeyDown } =
    useFormDialog({
      form,
      onSubmit,
      submitDisabled,
      submitting,
      submitOnEnter,
      step: dialogProps.step,
    });

  return (
    <FormProvider {...form}>
      <Dialog
        {...dialogProps}
        onClose={onClose}
        actions={
          <DialogActions
            type={actionsType}
            back={back}
            primary={
              <Tooltip label={submitTooltip}>
                <LoadingButton
                  type="submit"
                  form={formId}
                  size="large"
                  state={isPending ? "loading" : "default"}
                  loadingMessage={submitLoadingMessage}
                  disabled={isSubmitBlocked}
                  data-testid="modal-confirm-button">
                  {submitButtonText}
                </LoadingButton>
              </Tooltip>
            }
            secondary={
              <Button
                variant="outlined"
                size="large"
                onClick={onClose}
                data-testid="modal-cancel-button">
                {cancelButtonText}
              </Button>
            }
          />
        }>
        <Box
          component="form"
          id={formId}
          noValidate
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
          sx={FORM_SX}>
          {children}
        </Box>
      </Dialog>
    </FormProvider>
  );
};
